import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import CopyInProgressDialog from '../../components/muiLegacy/Dialogs/CopyInProgressDialog';
import CopyProjectDialog from '../../components/muiLegacy/Dialogs/CopyProjectDialog';
import NewProjectDialog from './components/NewProjectDialog/NewProjectDialog';
import TranslateInProgressDialog from '../../components/muiLegacy/Dialogs/TranslateInProgressDialog';
import TranslateProjectDialog from '../../components/muiLegacy/Dialogs/TranslateProjectDialog';
import LoadingScreen from '../../components/muiLegacy/LoadingScreen/LoadingScreen';
import VoxelButton from '../../components/voxel/Buttons/VoxelButton';
import VoxelPageContainer from '../../components/voxel/Containers/VoxelPageContainer';
import VoxelPanel from '../../components/voxel/Surfaces/VoxelPanel';
import ProjectTable from '../../components/muiLegacy/Tables/ProjectTable/ProjectTable';
import { ProjectGq } from '../../graphql/projects/models/ProjectGq';
import { ProjectTypeGq } from '../../graphql/projects/models/ProjectTypeGq';
import { projectsApiService } from '../../graphql/projects/projectsApiService';
import { GetProjectsCommandGq } from '../../graphql/projects/queries/useGetProjectsGq';
import { usersApiService } from '../../graphql/users/usersApiService';
import { useDebounceValue } from '../../logic/otherUtilities/useDebounceValue';
import useIsScrolling from '../../logic/otherUtilities/useIsScrolling';
import { ProjectTabChoices } from '../ProjectPage/ProjectPage';
import StartPageAppBar from './components/StartPageAppBar';
const deepEqual = require('deep-equal');

export interface StartPageProps { }

const StartPage: React.FC<StartPageProps> = () => {
  // Get user
  const {
    team,
    user,
    availableUsers,
    query: userQuery,
  } = usersApiService.queries.useGetCurrentUser();

  // Create project
  const [openCreateProjectDialog, setOpenCreateProjectDialog] = useState(false);

  // Stored filter logic
  const filter_version = 4; // IMPORTANT - THIS NEEDS TO CHANGE INCREASE THE MODEL CHANGES
  const initial_filter: GetProjectsCommandGq = {
    userOnly: false,
    showArchived: false,
    progressEnabled: false,
    searchTerm: '',
    sortBy: 'createTime',
    sortDirection: 'asc',
    page: 0,
    amountPerPage: 25,
    types: [ProjectTypeGq.NORMAL_SEARCH, ProjectTypeGq.MULTI_PROJECT],
  };
  const start_filter = useMemo(() => {
    const stored_filter_version = sessionStorage.getItem('start_filter_version');
    if (+stored_filter_version !== filter_version) {
      return initial_filter;
    }

    const stored_filter = JSON.parse(sessionStorage.getItem('start_filter_' + user?.team?.id));
    if (!stored_filter) {
      return initial_filter;
    }
    return stored_filter;
  }, []);

  const [filterState, setFilterState] = useState<GetProjectsCommandGq>(start_filter);

  // Update stored filter
  const debounced_filter = useDebounceValue(filterState, 300);

  const resetFilter = () => setFilterState(initial_filter);
  const pristineFilter = useMemo(
    (): boolean => !!deepEqual(initial_filter, filterState),
    [filterState]
  );

  useEffect(() => {
    sessionStorage.setItem('start_filter_version', filter_version.toString());
    sessionStorage.setItem('start_filter_' + user?.team?.id, JSON.stringify({
      ...filterState,
      page: 0,
      amountPerPage: 25
    }));
  }, [debounced_filter]);

  // Get data
  const projectsQuery = projectsApiService.queries.useGetProjects({
    ...filterState,
    searchTerm: debounced_filter.searchTerm, // We don't want the query to refresh several times per second (e.g. typing speed)
  });

  // Copy project
  const [copyProject, setCopyProject] = useState<ProjectGq>();
  const [copyInProgressId, setCopyInProgressId] = useState<number>();
  const handleCopyProjectSuccess = useCallback(
    (newProject: ProjectGq) =>
      projectsQuery.query.refetch().then(() => {
        setCopyProject(undefined);
        setCopyInProgressId(newProject.id);
      }),
    []
  );

  // Translate project
  const [translateProject, setTranslateProject] = useState<ProjectGq>();
  const [translateInProgressId, setTranslateProjectInProgressId] = useState<number>();
  const handleTranslateProjectSuccess = useCallback(
    (newProject: ProjectGq) =>
      projectsQuery.query.refetch().then(() => {
        setTranslateProject(undefined);
        setTranslateProjectInProgressId(newProject.id);
      }),
    []
  );

  useEffect(() => {
    const devPrefix = window.location.href.includes('localhost') ? 'Dev | ' : '';
    document.title = `${devPrefix}Voxel`;
  }, []);

  // History
  const history = useHistory();
  const isScrolling = useIsScrolling();

  // Redirect to incomplete setup steps if needed
  React.useEffect(() => {
    if (user?.team?.setupCompletedTeamMembers === false) {
      history.push('/register/members');
    }
  }, [user?.team]);

  // Render
  if ((!projectsQuery.data && projectsQuery.query.loading) || !user) {
    return <LoadingScreen />;
  }

  return (
    <>
      <StartPageAppBar
        team={team}
        user={user}
        isScrolling={isScrolling}
        availableUsers={availableUsers}
      />
      <VoxelPageContainer paddingTopClass="pt-0" maxWidthClass="lg:max-w-screen-2xl">
        <div className='flex flex-row justify-start ml-2 mb-8'>
          <VoxelButton
            title="Create project"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setOpenCreateProjectDialog(true)}
            disabled={!user.accessUserCanCreateProject}
            color="primary"
            size="lg"
          />
        </div>
        <VoxelPanel>
          <ProjectTable
            resetFilter={resetFilter}
            pristineFilter={pristineFilter}
            projectsData={projectsQuery.data}
            sortBy={filterState.sortBy}
            sortDirection={filterState.sortDirection}
            filterState={filterState}
            setFilterState={setFilterState}
            copyProject={setCopyProject}
            setShowCopyProgressId={setCopyInProgressId}
            setShowTranslateProgressId={setTranslateProjectInProgressId}
            translateProject={setTranslateProject}
            rowComponent="a"
            showOptions
            user={user}
          />
        </VoxelPanel>

        {user && openCreateProjectDialog && (
          <NewProjectDialog
            openDialog={openCreateProjectDialog}
            setOpenDialog={setOpenCreateProjectDialog}
            user={user}
          />
        )}
        {copyProject && (
          <CopyProjectDialog
            user={user}
            project={copyProject}
            close={() => setCopyProject(undefined)}
            onSuccess={handleCopyProjectSuccess}
          />
        )}
        {copyInProgressId && (
          <CopyInProgressDialog
            projectId={copyInProgressId}
            closeDialog={() => setCopyInProgressId(undefined)}
            goToProject={() => history.push(`/${copyInProgressId}/${ProjectTabChoices.Manage}`)}
          />
        )}
        {translateProject && !!user && (
          <TranslateProjectDialog
            project={translateProject}
            close={() => setTranslateProject(undefined)}
            onSuccess={handleTranslateProjectSuccess}
            user={user}
          />
        )}
        {translateInProgressId && (
          <TranslateInProgressDialog
            projectId={translateInProgressId}
            closeDialog={() => setTranslateProjectInProgressId(undefined)}
            goToProject={() =>
              history.push(`/${translateInProgressId}/${ProjectTabChoices.Manage}`)
            }
          />
        )}
      </VoxelPageContainer>
    </>
  );
};

export default StartPage;
